import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Box,
  Card,
  CardContent,
  Grid
} from '@material-ui/core';
import firebase from "firebase/app";
import "firebase/database";
import "firebase/storage";
import swal from 'sweetalert';
import TopMenu from './TopMenu';
import ShortUrlService from '../services/ShortUrlService';
import AuthenticatedUrlCache from '../services/AuthenticatedUrlCache';

// Utility to convert a timestamp to local datetime-local input string
function toLocalInputString(timestamp) {
  if (!timestamp) return '';
  const date = new Date(typeof timestamp === 'number'
    ? (timestamp < 1e12 ? timestamp * 1000 : timestamp)
    : Date.parse(timestamp));
  // Format as YYYY-MM-DDTHH:mm in local time
  const pad = n => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Utility to parse local input string to UTC timestamp
function parseLocalInputToUTCTimestamp(input) {
  if (!input) return null;
  // input is in 'YYYY-MM-DDTHH:mm' and is local time
  const date = new Date(input);
  return date.getTime();
}

  // Function to get display URL for images (handles both legacy and new)
  const getDisplayUrl = async (imageUrl) => {
    if (!imageUrl) return '';
    
    let finalUrl;
    
    // If it's a short URL (legacy), convert to full URL
    if (imageUrl.startsWith('q-')) {
      finalUrl = await ShortUrlService.toFullUrl(imageUrl);
    } else {
      // Otherwise, return as-is (should be full Firebase Storage URL)
      finalUrl = imageUrl;
    }
    
    // Handle Firebase Storage URLs with authentication
    if (finalUrl.includes('firebasestorage.googleapis.com')) {
      // Always use authenticated access for secure storage
      try {
        const authenticatedUrl = await AuthenticatedUrlCache.getAuthenticatedUrl(finalUrl);
        return authenticatedUrl;
      } catch (error) {
        console.error('Failed to get authenticated URL:', error);
        // Return the original URL as fallback instead of throwing error
        return finalUrl;
      }
    }
    
    return finalUrl;
  };

// Authenticated Image Component
const AuthenticatedImage = ({ imageUrl, alt, style }) => {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);
        const authenticatedUrl = await getDisplayUrl(imageUrl);
        setSrc(authenticatedUrl);
      } catch (err) {
        console.error('Failed to load authenticated image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (imageUrl) {
      loadImage();
    }
  }, [imageUrl]);

  if (loading) {
    return <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>Loading image...</div>;
  }

  if (error) {
    return (
      <div style={{ 
        ...style, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        background: '#ffebee', 
        color: '#d32f2f',
        fontSize: '12px',
        textAlign: 'center',
        padding: '8px'
      }}>
        🔒 Authentication required<br/>
        Please login to view this image
      </div>
    );
  }

  return (
    <img 
      src={src} 
      alt={alt} 
      style={style}
      onError={(e) => {
        console.error('Image failed to load:', src);
        e.target.style.display = 'none';
      }}
    />
  );
};

const ExamCodeEntry = () => {
  const [examCode, setExamCode] = useState('');
  const [examData, setExamData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editFields, setEditFields] = useState({ start: '', end: '', timer: '' });


  // Initialize ShortUrlService for legacy short URL conversion
  useEffect(() => {
    const firebaseConfig = firebase.app().options;
    ShortUrlService.autoInit(firebaseConfig);
  }, []);

  // Add authentication listener to ensure user is authenticated
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      // User authentication state tracked silently
    });

    return () => unsubscribe();
  }, []);



  const handleSearch = async () => {
    if (!examCode.trim()) {
      setError('Enter an assessment code to proceed');
      return;
    }
    setLoading(true);
    setError('');
    setExamData(null);
    try {
      const examRef = firebase.database().ref(`TestPapers/${examCode.trim()}`);
      const snapshot = await examRef.once('value');
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        // Normalize exam data to handle both old and new formats
        const normalizedExamData = {
          code: examCode.trim(),
          title: data.title || data.examTitle,
          timer: data.timer,
          start: data.start,
          end: data.end,
          sections: data.sections ? data.sections.map(section => ({
            id: section.id,
            title: section.t || section.title,
            weightage: section.w || section.weightage,
                        questions: section.q || section.questions ? (section.q || section.questions).map(q => ({
                id: q.id,
                type: q.type,
                text: q.text,
                imageUrl: q.imageUrl || '',
                problemDescription: q.pd || q.problemDescription || '',
                instructions: q.inst || q.instructions || '',
                boilerplateCode: q.bc || q.boilerplateCode || '',
                evaluationKeywords: q.ek || q.evaluationKeywords || '',
                evaluationPoints: (() => {
                    // Combine evaluationPoints and evaluationKeywords for backward compatibility
                    const evalPoints = q.evaluationPoints || '';
                    const evalKeywords = q.ek || q.evaluationKeywords || '';
                    if (evalPoints && evalKeywords) {
                        // Both exist - combine them (remove duplicates)
                        const pointsArray = typeof evalPoints === 'string' 
                            ? evalPoints.split(',').map(p => p.trim()).filter(Boolean)
                            : Array.isArray(evalPoints) ? evalPoints : [];
                        const keywordsArray = typeof evalKeywords === 'string'
                            ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean)
                            : Array.isArray(evalKeywords) ? evalKeywords : [];
                        const combined = [...pointsArray, ...keywordsArray];
                        return [...new Set(combined)].join(', ');
                    }
                    return evalPoints || evalKeywords || '';
                })(),
                enableAIEvaluation: q.ai !== undefined ? q.ai : (q.enableAIEvaluation || false),
                maxScore: q.maxScore,
                options: q.opts || q.options ? (q.opts || q.options).map(opt => ({
                    id: opt.id,
                    text: opt.t || opt.text,
                    marks: opt.m !== undefined ? opt.m : (opt.marks || 0),
                    isCorrect: opt.c !== undefined ? opt.c : (opt.isCorrect || false)
                })) : [],
    
                psycoAnswer: q.psycoAnswer || '',
                scenarioOptions: q.scenarioOptions || []
            })) : []
          })) : []
        };
        
        setExamData(normalizedExamData);
        const startISO = data.start
          ? new Date(
              typeof data.start === 'number'
                ? (data.start < 1e12 ? data.start * 1000 : data.start)
                : Date.parse(data.start)
            ).toISOString().slice(0,16)
          : '';
        const endISO = data.end
          ? new Date(
              typeof data.end === 'number'
                ? (data.end < 1e12 ? data.end * 1000 : data.end)
                : Date.parse(data.end)
            ).toISOString().slice(0,16)
          : '';
        setEditFields({
          start: toLocalInputString(data.start),
          end: toLocalInputString(data.end),
          timer: data.timer || ''
        });
      } else {
        setError('Assessment not found. Verify the assessment code and try again.');
      }
    } catch (error) {
      setError('Unable to retrieve assessment data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Defensive initialization for editFields
  const safeEditFields = {
    start: typeof editFields.start === 'string' ? editFields.start : '',
    end: typeof editFields.end === 'string' ? editFields.end : '',
    timer: typeof editFields.timer === 'string' || typeof editFields.timer === 'number' ? String(editFields.timer ?? '') : ''
  };

  return (
    <div style={{ paddingTop: 70, minHeight: '100vh', background: '#f7f9fb' }}>
      <TopMenu showHome showLogout />
      <div className="search-container">
        <Container maxWidth="sm" style={{ marginTop: 24, marginBottom: 32 }}>
          <Paper elevation={3} style={{ padding: 28, marginBottom: 28, borderRadius: 14, boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
            <Typography variant="h5" gutterBottom style={{ fontWeight: 700, color: '#004085', marginBottom: 8 }}>
              Enter Assessment Code
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Enter the assessment code to view assessment details and submissions.
            </Typography>
            <Box display="flex" alignItems="center" style={{ marginTop: 20 }}>
              <TextField
                label="Assessment Code"
                value={examCode}
                onChange={(e) => setExamCode(e.target.value)}
                variant="outlined"
                fullWidth
                placeholder="e.g., ASSESS001"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{ background: '#fff' }}
                InputProps={{ style: { height: 48, fontSize: 18 } }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading}
                style={{
                  height: 40,
                  minWidth: 140,
                  maxWidth: 140,
                  fontWeight: 700,
                  fontSize: 18,
                  marginLeft: 12,
                  boxShadow: '0 2px 8px rgba(33,150,243,0.08)',
                  transition: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  textTransform: 'none'
                }}
              >
                <span>{loading ? 'Searching...' : 'Search'}</span>
              </Button>

            </Box>
            {error && (
              <div style={{ 
                marginTop: 16, 
                padding: 12, 
                backgroundColor: '#ffebee', 
                border: '1px solid #f44336', 
                borderRadius: 4, 
                color: '#d32f2f' 
              }}>
                {error}
              </div>
            )}

          </Paper>
        </Container>
      </div>
      <div className="exam-details-container" style={{padding: '0 20px'}}>
        {examData && (
          <Box mt={4} mb={4} style={{ background: '#fff', borderRadius: 10, padding: '36px 48px', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', margin: '40px auto 40px 0', textAlign: 'left' }}>
            <div>
              <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 12 }}>Update Assessment Timing</Typography>
              <Box display="flex" alignItems="center" gap={2} mb={4}>
                <TextField
                  label="Start Date & Time"
                  type="datetime-local"
                  value={editFields.start || ''}
                  onChange={e => {
                    const value = e.target.value;
                    setEditFields(f => ({ ...f, start: value }));
                  }}
                  variant="outlined"
                  style={{ minWidth: 220, marginRight: 12 }}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  label="End Date & Time"
                  type="datetime-local"
                  value={editFields.end || ''}
                  onChange={e => {
                    const value = e.target.value;
                    setEditFields(f => ({ ...f, end: value }));
                  }}
                  variant="outlined"
                  style={{ minWidth: 220, marginRight: 12 }}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  label="Duration (min)"
                  type="number"
                  value={editFields.timer || ''}
                  onChange={e => {
                    const value = e.target.value;
                    setEditFields(f => ({ ...f, timer: value }));
                  }}
                  variant="outlined"
                  style={{ minWidth: 120, marginRight: 12 }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  style={{
                    height: 40,
                    minWidth: 160,
                    maxWidth: 160,
                    fontWeight: 700,
                    marginLeft: 0,
                    marginTop: 0,
                    boxShadow: '0 2px 8px rgba(33,150,243,0.08)',
                    transition: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    textTransform: 'none'
                  }}
                  onClick={async () => {
                    try {
                      const updates = {
                        start: editFields.start ? parseLocalInputToUTCTimestamp(editFields.start) : null,
                        end: editFields.end ? parseLocalInputToUTCTimestamp(editFields.end) : null,
                        timer: editFields.timer ? parseInt(editFields.timer) : null
                      };
                      const updatePath = `TestPapers/${examData.code}`;
                      await firebase.database().ref(`TestPapers/${examData.code}`).update(updates);
                      swal('Success', 'Assessment timing updated!', 'success');
                      const examRef = firebase.database().ref(`TestPapers/${examData.code}`);
                      const snapshot = await examRef.once('value');
                      if (snapshot.exists()) {
                        const data = snapshot.val();
                        setEditFields({
                          start: toLocalInputString(data.start),
                          end: toLocalInputString(data.end),
                          timer: data.timer || ''
                        });
                        setExamData({
                          code: examData.code,
                          ...data
                        });
                      }
                    } catch (err) {
                      swal('Error', 'Failed to update assessment timing', 'error');
                    }
                  }}
                >
                  Submit Changes
                </Button>
              </Box>
            </div>
            <Typography variant="h4" style={{ color: '#004085', fontWeight: 800, marginBottom: 18, letterSpacing: 0.5 }}>
              Assessment Data
            </Typography>
            <div style={{ marginBottom: 24 }}>
              <Typography variant="h5" style={{ fontWeight: 700, color: '#1976d2', marginBottom: 4 }}>
                {examData.title || examData.examTitle || `Assessment ${examData.code}`}
              </Typography>
              <Typography variant="body1" style={{ color: '#444', marginBottom: 2 }}>
                <b>Assessment Code:</b> {examData.code}
              </Typography>
              <Typography variant="body1" style={{ color: '#444', marginBottom: 2 }}>
                <b>Duration:</b> {examData.timer} minutes
              </Typography>
            </div>
            <div style={{ borderTop: '1px solid #eee', margin: '24px 0 16px 0' }} />
            {examData.sections && (examData.sections || examData.sections).map((section, sidx) => (
              <Box key={section.id || sidx} mb={5}>
                <Typography variant="h6" style={{ color: '#1976d2', fontWeight: 700, marginBottom: 10, letterSpacing: 0.2 }}>
                  Section {sidx + 1}{section.title ? `: ${section.title}` : ''} {section.weightage ? <span style={{ color: '#888', fontWeight: 400 }}>({section.weightage}%)</span> : ''}
                </Typography>
                {(section.q || section.questions).map((q, qidx) => (
                  <Box key={q.id || qidx} mb={4} ml={2}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 6, color: '#222' }}>
                      Q{qidx + 1}. {q.text || q.problemDescription}
                    </Typography>
                    {q.imageUrl && (
                      <div style={{ marginBottom: 12 }}>
                        <AuthenticatedImage 
                          imageUrl={q.imageUrl}
                          alt={`Question ${qidx + 1}`}
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: 300, 
                            border: '1px solid #ddd',
                            borderRadius: 8,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                      </div>
                    )}
                    {q.instructions && (
                      <Typography variant="body2" style={{ color: '#555', marginBottom: 4, fontStyle: 'italic' }}>
                        {q.instructions}
                      </Typography>
                    )}
                    {q.type === 'mcq-single' && (
                      <ul style={{ margin: 0, paddingLeft: 24, listStyle: 'disc' }}>
                        {(q.options || q.opts).map(opt => (
                          <li key={opt.id} style={{
                            color: opt.c || opt.isCorrect ? '#2e7d32' : '#222',
                            fontWeight: opt.c || opt.isCorrect ? 700 : 400,
                            fontSize: 18,
                            marginBottom: 2
                          }}>
                            {opt.text || opt.t}
                          </li>
                        ))}
                      </ul>
                    )}
                    {q.type === 'mcq-multi' && (
                      <ul style={{ margin: 0, paddingLeft: 24, listStyle: 'disc' }}>
                        {(q.options || q.opts).map(opt => (
                          <li key={opt.id} style={{
                            color: opt.c || opt.isCorrect ? '#2e7d32' : '#222',
                            fontWeight: opt.c || opt.isCorrect ? 700 : 400,
                            fontSize: 18,
                            marginBottom: 2
                          }}>
                            {opt.text || opt.t}
                          </li>
                        ))}
                      </ul>
                    )}
                    {q.type === 'text' && (
                      <Typography variant="body1" style={{ color: '#888', fontStyle: 'italic', marginTop: 6 }}>
                        [Text Answer]
                      </Typography>
                    )}
                    {q.type === 'scenario-text' && (
                      <Box mt={1}>
                        <Typography variant="body1" style={{ color: '#333', marginBottom: 8, fontWeight: 600 }}>
                          <span style={{ color: '#1976d2' }}>📋 Scenario Options:</span>
                        </Typography>
                        {(q.scenarioOptions || []).map((option, optIdx) => (
                          <Box key={optIdx} mb={3} p={3} style={{ 
                            border: '2px solid #e3f2fd', 
                            borderRadius: 8, 
                            background: '#f8fbff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}>
                            <Typography variant="h6" style={{ 
                              fontWeight: 700, 
                              color: '#1976d2', 
                              marginBottom: 8,
                              fontSize: '1.1em'
                            }}>
                              🎯 Option {optIdx + 1}: {option.title}
                            </Typography>
                            
                            {option.description && (
                              <Typography variant="body2" style={{ 
                                color: '#555', 
                                marginBottom: 8, 
                                lineHeight: 1.5,
                                background: '#fff',
                                padding: '8px 12px',
                                borderRadius: 4,
                                border: '1px solid #e0e0e0'
                              }}>
                                <span style={{ fontWeight: 600, color: '#333' }}>📝 Scenario:</span> {option.description}
                              </Typography>
                            )}
                            
                            {option.evaluationPoints && option.evaluationPoints.length > 0 && (
                              <Box mt={2}>
                                <Typography variant="body2" style={{ 
                                  fontWeight: 600, 
                                  color: '#2e7d32', 
                                  marginBottom: 6,
                                  fontSize: '0.9em'
                                }}>
                                  📊 Evaluation Criteria ({option.evaluationPoints.length} points):
                                </Typography>
                                <Box style={{ 
                                  background: '#f1f8e9', 
                                  padding: '8px 12px', 
                                  borderRadius: 6,
                                  border: '1px solid #c8e6c9'
                                }}>
                                  {option.evaluationPoints.map((point, pointIdx) => (
                                    <Typography key={pointIdx} variant="body2" style={{ 
                                      color: '#2e7d32', 
                                      marginBottom: 4,
                                      fontSize: '0.85em',
                                      fontWeight: 500,
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '6px'
                                    }}>
                                      <span style={{ 
                                        fontSize: '0.8em', 
                                        color: '#4caf50',
                                        marginTop: '2px'
                                      }}>•</span>
                                      {point}
                                    </Typography>
                                  ))}
                                </Box>
                              </Box>
                            )}
                            
                            {option.maxScore && (
                              <Typography variant="body2" style={{ 
                                fontWeight: 600, 
                                color: '#ff9800', 
                                marginTop: 8,
                                fontSize: '0.9em',
                                background: '#fff3e0',
                                padding: '4px 8px',
                                borderRadius: 4,
                                display: 'inline-block',
                                border: '1px solid #ffcc02'
                              }}>
                                🏆 Max Score: {option.maxScore} marks
                              </Typography>
                            )}
                          </Box>
                        ))}
                        
                        <Box mt={2} p={2} style={{ 
                          background: '#e8f5e8', 
                          borderRadius: 6, 
                          border: '1px solid #4caf50' 
                        }}>
                          <Typography variant="body2" style={{ 
                            color: '#2e7d32', 
                            fontWeight: 600,
                            fontSize: '0.9em',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            📝 <span>Students will select one scenario and write an email response that addresses the evaluation criteria</span>
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {q.type === 'code' && (
                      <Box mt={1}>
                        {q.problemDescription && (
                          <Typography variant="body1" style={{ color: '#333', marginBottom: 6 }}>
                            <span style={{ fontWeight: 500 }}>Problem:</span> {q.problemDescription}
                          </Typography>
                        )}
                        {q.instructions && (
                          <Typography variant="body2" style={{ color: '#555', marginBottom: 4, fontStyle: 'italic' }}>
                            {q.instructions}
                          </Typography>
                        )}
                        {q.boilerplateCode && (
                          <Box mt={2}>
                            <Typography variant="body2" style={{ color: '#888', marginBottom: 2 }}>[Boilerplate Code]</Typography>
                            <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 15, overflowX: 'auto' }}>
                              {q.boilerplateCode}
                            </pre>
                          </Box>
                        )}
                      </Box>
                    )}

                    {q.type === 'video' && (
                      <Typography variant="body1" style={{ color: '#888', fontStyle: 'italic', marginTop: 6 }}>
                        [Video Response]
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}
      </div>
    </div>
  );
};

export default ExamCodeEntry; 